/**
 * Express Setup Page
 *
 * Go-live checklist for express clinics. Clinic admins fill in checklist
 * items and provide requested info; super admins additionally control the
 * Express flag and confirm the clinic as Live.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { expressSetupAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
  Button, Badge, Textarea,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui'
import { Loader2, AlertCircle, Zap, CheckCircle2, Rocket, Paperclip, X, Copy, Check } from 'lucide-react'

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: '#6B7280' },
  in_progress: { label: 'In Progress', color: '#3B82F6' },
  blocked: { label: 'Blocked', color: '#EF4444' },
  completed: { label: 'Completed', color: '#22C55E' },
  na: { label: 'N/A', color: '#A855F7' },
}

const WAITING_ON_CONFIG = {
  clinic: { label: 'Waiting on Clinic', short: 'Clinic', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-400' },
  express: { label: 'Waiting on 5axis', short: '5axis', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-400' },
}

const DNS_RECORD_TYPES = ['A', 'AAAA', 'CNAME', 'TXT', 'MX', 'NS', 'SRV']

// Format DNS records as a Slack-ready message: bold title + a monospace
// code block with one labeled block per record. Per-record (rather than a
// wide aligned table) keeps long values (TXT/DKIM/CNAME) from wrapping and
// breaking column alignment when pasted into Slack.
function formatDnsForSlack(taskTitle, rows) {
  const labels = ['Type', 'Host', 'Value']
  const width = Math.max(...labels.map(l => l.length)) + 1 // ':' suffix
  const block = rows.map(r => [
    `${('Type:').padEnd(width)} ${r.type || ''}`,
    `${('Host:').padEnd(width)} ${r.host || ''}`,
    `${('Value:').padEnd(width)} ${r.value || ''}`,
  ].join('\n')).join('\n\n')
  return [
    `*${taskTitle}*`,
    '```',
    block,
    '```',
  ].join('\n')
}

function DnsRecordsTable({ task, editable, onSave, saving }) {
  const [rows, setRows] = useState(task.dnsRecords || [])
  const [dirty, setDirty] = useState(false)
  const [copiedIdx, setCopiedIdx] = useState(null)
  const [copiedSlack, setCopiedSlack] = useState(false)

  const handleCopySlack = async () => {
    try {
      await navigator.clipboard.writeText(formatDnsForSlack(task.title, rows))
      setCopiedSlack(true)
      setTimeout(() => setCopiedSlack(false), 2000)
    } catch { /* clipboard unavailable */ }
  }

  // Re-sync when the task is refreshed externally
  useEffect(() => {
    setRows(task.dnsRecords || [])
    setDirty(false)
  }, [task.dnsRecords])

  const updateRow = (idx, field, value) => {
    setRows(prev => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)))
    setDirty(true)
  }

  const addRow = () => {
    setRows(prev => [...prev, { type: 'CNAME', host: '', value: '' }])
    setDirty(true)
  }

  const removeRow = (idx) => {
    setRows(prev => prev.filter((_, i) => i !== idx))
    setDirty(true)
  }

  const handleSave = () => {
    const cleaned = rows.filter(r => r.host.trim() || r.value.trim())
    onSave({ dnsRecords: cleaned })
  }

  const handleCopy = async (value, idx) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopiedIdx(idx)
      setTimeout(() => setCopiedIdx(null), 1500)
    } catch { /* clipboard unavailable */ }
  }

  if (!editable && rows.length === 0) {
    return (
      <p className="mt-3 text-sm text-muted-foreground italic">
        Waiting for 5axis to provide DNS records…
      </p>
    )
  }

  return (
    <div className="mt-3 space-y-2">
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="min-w-full divide-y divide-border text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase w-24">Type</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase">Host / Name</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase">Value</th>
              <th className="px-2 py-2 w-10" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border bg-background">
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-4 text-center text-muted-foreground text-sm">
                  No records yet — add the DNS records the clinic needs to create.
                </td>
              </tr>
            )}
            {rows.map((row, idx) => (
              <tr key={idx}>
                {editable ? (
                  <>
                    <td className="px-2 py-1.5">
                      <select
                        value={row.type}
                        onChange={(e) => updateRow(idx, 'type', e.target.value)}
                        className="w-full h-8 px-2 text-sm rounded-md border border-input bg-background"
                      >
                        {DNS_RECORD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-1.5">
                      <input
                        value={row.host}
                        onChange={(e) => updateRow(idx, 'host', e.target.value)}
                        placeholder="intake.clinic.com"
                        className="w-full h-8 px-2 text-sm font-mono rounded-md border border-input bg-background"
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <input
                        value={row.value}
                        onChange={(e) => updateRow(idx, 'value', e.target.value)}
                        placeholder="target.5axis.health"
                        className="w-full h-8 px-2 text-sm font-mono rounded-md border border-input bg-background"
                      />
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <button
                        type="button"
                        onClick={() => removeRow(idx)}
                        className="p-1 text-muted-foreground hover:text-destructive"
                        title="Remove record"
                      >
                        ✕
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-3 py-2 font-medium">{row.type}</td>
                    <td className="px-3 py-2 font-mono text-xs">{row.host}</td>
                    <td className="px-3 py-2 font-mono text-xs break-all">{row.value}</td>
                    <td className="px-2 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => handleCopy(row.value, idx)}
                        className="text-xs text-primary hover:underline whitespace-nowrap"
                        title="Copy value"
                      >
                        {copiedIdx === idx ? 'Copied!' : 'Copy'}
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-2">
        {editable && (
          <>
            <Button type="button" variant="outline" size="sm" onClick={addRow} disabled={saving}>
              + Add Record
            </Button>
            {dirty && (
              <Button type="button" size="sm" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save Records'}
              </Button>
            )}
          </>
        )}
        {rows.some(r => r.host?.trim() || r.value?.trim()) && (
          <Button type="button" variant="outline" size="sm" onClick={handleCopySlack} title="Copy as a Slack-formatted message">
            {copiedSlack ? <Check className="w-3.5 h-3.5 mr-1.5" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
            {copiedSlack ? 'Copied!' : 'Copy for Slack'}
          </Button>
        )}
      </div>
    </div>
  )
}

function formatDate(d) {
  if (!d) return ''
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatDateTime(d) {
  if (!d) return ''
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

// Human-readable activity entry text
function describeActivity(entry) {
  const t = entry.taskTitle ? `"${entry.taskTitle}"` : ''
  switch (entry.action) {
    case 'status_changed':
      return <>changed {t} status: <span className="font-medium">{entry.from}</span> → <span className="font-medium">{entry.to}</span></>
    case 'response_updated':
      return <>updated info on {t}{entry.to ? <>: <span className="italic">"{entry.to}"</span></> : ''}</>
    case 'waiting_on_changed':
      return <>moved {t} to waiting on <span className="font-medium">{entry.to === 'express' ? '5axis' : 'clinic'}</span></>
    case 'dns_records_updated':
      return <>updated DNS records on {t} ({entry.from} → {entry.to})</>
    case 'attachment_added':
      return <>uploaded <span className="font-medium">{entry.to}</span> to {t}</>
    case 'attachment_deleted':
      return <>removed <span className="font-medium">{entry.from}</span> from {t}</>
    case 'task_added':
      return <>added custom task <span className="font-medium">"{entry.to}"</span></>
    case 'task_deleted':
      return <>deleted custom task <span className="font-medium">"{entry.from}"</span></>
    case 'express_flag_changed':
      return <>set express flag to <span className="font-medium">{entry.to}</span></>
    case 'live_flag_changed':
      return entry.to === 'true' ? <>marked the clinic <span className="font-medium">LIVE</span> 🚀</> : <>marked the clinic <span className="font-medium">not live</span></>
    default:
      return <>{entry.action} {t}</>
  }
}

function formatBytes(bytes) {
  if (bytes == null) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function AddTaskForm({ onAdd, onCancel }) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [waitingOn, setWaitingOn] = useState('clinic')
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async () => {
    if (!title.trim()) return
    setSubmitting(true)
    const ok = await onAdd({ title: title.trim(), description: description.trim(), waitingOn })
    setSubmitting(false)
    if (ok) {
      setTitle('')
      setDescription('')
      setWaitingOn('clinic')
      onCancel()
    }
  }

  return (
    <div className="border border-dashed border-border rounded-lg p-4 space-y-3 bg-muted/20">
      <h4 className="text-sm font-semibold text-foreground">Add Custom Task</h4>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Task title *"
        className="w-full h-9 px-3 text-sm rounded-md border border-input bg-background"
        autoFocus
      />
      <Textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Description / instructions (optional)"
        className="min-h-[60px] text-sm"
      />
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Waiting on:</span>
          <Select value={waitingOn} onValueChange={setWaitingOn}>
            <SelectTrigger className="w-28 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(WAITING_ON_CONFIG).map(([value, cfg]) => (
                <SelectItem key={value} value={value}>{cfg.short}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={handleSubmit} disabled={submitting || !title.trim()}>
            {submitting ? 'Adding…' : 'Add Task'}
          </Button>
        </div>
      </div>
    </div>
  )
}

function TaskRow({ task, onUpdate, onUpload, onDownload, onDeleteAttachment, onDeleteTask, onVerifySite, saving, isSuperAdmin, canEdit }) {
  const [response, setResponse] = useState(task.response || '')
  const fileInputRef = useRef(null)
  const isDone = ['completed', 'na'].includes(task.status)

  // Keep local response in sync if the task is refreshed externally
  useEffect(() => { setResponse(task.response || '') }, [task.response])

  const handleResponseBlur = () => {
    if (response !== (task.response || '')) {
      onUpdate(task.key, { response })
    }
  }

  return (
    <div className={`border border-border rounded-lg p-4 ${isDone ? 'bg-muted/30' : 'bg-background'}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className={`text-sm font-semibold ${isDone ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
              {task.title}
            </h4>
            {!isDone && (
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${WAITING_ON_CONFIG[task.waitingOn || 'clinic'].className}`}>
                {WAITING_ON_CONFIG[task.waitingOn || 'clinic'].label}
              </span>
            )}
            {task.isCustom && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-muted text-muted-foreground border border-border">
                Custom
              </span>
            )}
          </div>
          {task.description && (
            <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
          )}
          {task.status === 'completed' && task.completedBy && (
            <p className="text-xs text-muted-foreground mt-1.5">
              Completed by {task.completedBy} on {formatDate(task.completedAt)}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isDone && canEdit && (
            <Select
              value={task.waitingOn || 'clinic'}
              onValueChange={(waitingOn) => onUpdate(task.key, { waitingOn })}
              disabled={saving}
            >
              <SelectTrigger className="w-28 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(WAITING_ON_CONFIG).map(([value, cfg]) => (
                  <SelectItem key={value} value={value}>{cfg.short}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {canEdit ? (
            <Select
              value={task.status}
              onValueChange={(status) => onUpdate(task.key, { status })}
              disabled={saving}
            >
              <SelectTrigger className="w-36 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_CONFIG).map(([value, cfg]) => (
                  <SelectItem key={value} value={value}>
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: cfg.color }} />
                      {cfg.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span
              className="px-2 py-1 text-xs font-medium rounded inline-flex items-center gap-1.5 border border-border"
            >
              <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: STATUS_CONFIG[task.status]?.color }} />
              {STATUS_CONFIG[task.status]?.label || task.status}
            </span>
          )}
          {task.isCustom && isSuperAdmin && (
            <button
              type="button"
              onClick={() => onDeleteTask(task)}
              className="p-1.5 text-muted-foreground hover:text-destructive"
              title="Delete custom task"
              disabled={saving}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Notes/info and actions are hidden once the task is done */}
      {!isDone && (
      <>
      {/* Site liveness check */}
      {task.key === 'site-live-check' && canEdit && (
        <div className="mt-3">
          <Button type="button" variant="outline" size="sm" onClick={onVerifySite} disabled={saving}>
            {saving ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
            )}
            Verify Now
          </Button>
        </div>
      )}

      {/* Attachments (logos, fonts, renders, ...) */}
      <div className="mt-3">
        {(task.attachments || []).length > 0 && (
          <ul className="space-y-1 mb-2">
            {task.attachments.map((att) => (
              <li key={att.key} className="flex items-center gap-2 text-sm">
                <Paperclip className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <button
                  type="button"
                  onClick={() => onDownload(task.key, att)}
                  className="text-primary hover:underline truncate"
                  title="Download"
                >
                  {att.filename}
                </button>
                <span className="text-xs text-muted-foreground shrink-0">
                  {formatBytes(att.size)} · {att.uploadedBy} · {formatDate(att.uploadedAt)}
                </span>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => onDeleteAttachment(task.key, att)}
                    className="p-0.5 text-muted-foreground hover:text-destructive shrink-0"
                    title="Delete file"
                    disabled={saving}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {canEdit && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files || [])
                if (files.length > 0) onUpload(task.key, files)
                e.target.value = ''
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={saving}
            >
              {saving ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <Paperclip className="w-3.5 h-3.5 mr-1.5" />
              )}
              Attach File
            </Button>
          </>
        )}
      </div>

      {/* DNS records table (5axis provides, clinic applies) */}
      {task.hasDnsTable && (
        <DnsRecordsTable
          task={task}
          editable={isSuperAdmin}
          onSave={(data) => onUpdate(task.key, data)}
          saving={saving}
        />
      )}

      {canEdit ? (
        <div className="mt-3">
          <Textarea
            value={response}
            onChange={(e) => setResponse(e.target.value)}
            onBlur={handleResponseBlur}
            placeholder="Provide the requested info / notes here…"
            className="min-h-[60px] text-sm"
            disabled={saving}
          />
        </div>
      ) : response ? (
        <div className="mt-3 text-sm text-foreground whitespace-pre-wrap rounded-md border border-border bg-muted/30 p-3">
          {response}
        </div>
      ) : null}
      </>
      )}
    </div>
  )
}

export default function ExpressSetupPage() {
  const { user, currentClinicId } = useAuthStore()
  const isSuperAdmin = user?.role === 'super_admin'
  // Admins can edit; everyone else with clinic access views read-only
  const canEdit = ['super_admin', 'clinic_admin'].includes(user?.role)

  const [setup, setSetup] = useState(null)
  const [loading, setLoading] = useState(true)
  const [savingKey, setSavingKey] = useState(null)
  const [error, setError] = useState(null)
  const [waitingFilter, setWaitingFilter] = useState('all') // all | clinic | express
  const [showAddTask, setShowAddTask] = useState(false)
  const [showActivity, setShowActivity] = useState(false)
  const [activityLimit, setActivityLimit] = useState(25)

  const handleToggleActivity = async () => {
    const opening = !showActivity
    setShowActivity(opening)
    if (opening) {
      // Refresh so the log includes changes made since page load
      try {
        const res = await expressSetupAPI.get(currentClinicId)
        if (res.data?.success) setSetup(res.data.data)
      } catch { /* keep showing what we have */ }
    }
  }

  const fetchSetup = useCallback(async () => {
    if (!currentClinicId) {
      setSetup(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await expressSetupAPI.get(currentClinicId)
      if (res.data?.success) setSetup(res.data.data)
    } catch (err) {
      console.error('[EXPRESS SETUP] Error:', err)
      setError(err.response?.data?.error?.message || 'Failed to load express setup')
    } finally {
      setLoading(false)
    }
  }, [currentClinicId])

  useEffect(() => { fetchSetup() }, [fetchSetup])

  const handleTaskUpdate = async (key, data) => {
    setSavingKey(key)
    try {
      const res = await expressSetupAPI.updateTask(currentClinicId, key, data)
      if (res.data?.success) {
        const { task, progress } = res.data.data
        applyTaskUpdate(key, task, progress)
      }
    } catch (err) {
      console.error('[EXPRESS SETUP] Task update error:', err)
      setError(err.response?.data?.error?.message || 'Failed to update task')
    } finally {
      setSavingKey(null)
    }
  }

  const applyTaskUpdate = (key, task, progress) => {
    setSetup(prev => ({
      ...prev,
      progress,
      tasks: prev.tasks.map(t => (t.key === key ? task : t)),
    }))
  }

  const handleUpload = async (key, files) => {
    setSavingKey(key)
    setError(null)
    try {
      let last = null
      for (const file of files) {
        last = await expressSetupAPI.uploadAttachment(currentClinicId, key, file)
      }
      if (last?.data?.success) {
        const { task, progress } = last.data.data
        applyTaskUpdate(key, task, progress)
      }
    } catch (err) {
      console.error('[EXPRESS SETUP] Upload error:', err)
      setError(err.response?.data?.error?.message || 'Failed to upload file')
    } finally {
      setSavingKey(null)
    }
  }

  const handleDownload = async (taskKey, attachment) => {
    try {
      const res = await expressSetupAPI.downloadAttachment(currentClinicId, taskKey, attachment.key)
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = url
      a.download = attachment.filename
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('[EXPRESS SETUP] Download error:', err)
      setError('Failed to download file')
    }
  }

  const handleDeleteAttachment = async (taskKey, attachment) => {
    if (!window.confirm(`Delete ${attachment.filename}?`)) return
    setSavingKey(taskKey)
    try {
      const res = await expressSetupAPI.deleteAttachment(currentClinicId, taskKey, attachment.key)
      if (res.data?.success) {
        const { task, progress } = res.data.data
        applyTaskUpdate(taskKey, task, progress)
      }
    } catch (err) {
      console.error('[EXPRESS SETUP] Delete attachment error:', err)
      setError(err.response?.data?.error?.message || 'Failed to delete file')
    } finally {
      setSavingKey(null)
    }
  }

  const handleVerifySite = async () => {
    setSavingKey('site-live-check')
    setError(null)
    try {
      const res = await expressSetupAPI.verifySite(currentClinicId)
      if (res.data?.success) {
        const { task, progress, result } = res.data.data
        applyTaskUpdate('site-live-check', task, progress)
        if (!result.ok) {
          setError(`Site check failed: ${result.httpStatus ? `HTTP ${result.httpStatus}` : result.error} (${result.url})`)
        }
      }
    } catch (err) {
      console.error('[EXPRESS SETUP] Verify site error:', err)
      setError(err.response?.data?.error?.message || 'Failed to verify site')
    } finally {
      setSavingKey(null)
    }
  }

  const handleAddTask = async (data) => {
    setError(null)
    try {
      const res = await expressSetupAPI.addTask(currentClinicId, data)
      if (res.data?.success) {
        const { task, progress } = res.data.data
        setSetup(prev => ({ ...prev, progress, tasks: [...prev.tasks, task] }))
        return true
      }
    } catch (err) {
      console.error('[EXPRESS SETUP] Add task error:', err)
      setError(err.response?.data?.error?.message || 'Failed to add task')
    }
    return false
  }

  const handleDeleteTask = async (task) => {
    if (!window.confirm(`Delete custom task "${task.title}"? Its uploaded files will also be removed.`)) return
    setSavingKey(task.key)
    setError(null)
    try {
      const res = await expressSetupAPI.deleteTask(currentClinicId, task.key)
      if (res.data?.success) {
        const { progress } = res.data.data
        setSetup(prev => ({ ...prev, progress, tasks: prev.tasks.filter(t => t.key !== task.key) }))
      }
    } catch (err) {
      console.error('[EXPRESS SETUP] Delete task error:', err)
      setError(err.response?.data?.error?.message || 'Failed to delete task')
    } finally {
      setSavingKey(null)
    }
  }

  const handleFlagsUpdate = async (flags) => {
    try {
      const res = await expressSetupAPI.updateFlags(currentClinicId, flags)
      if (res.data?.success) setSetup(res.data.data)
    } catch (err) {
      console.error('[EXPRESS SETUP] Flags update error:', err)
      setError(err.response?.data?.error?.message || 'Failed to update flags')
    }
  }

  const handleToggleLive = () => {
    if (!setup) return
    if (!setup.isLive) {
      const { done, total } = setup.progress || {}
      const incomplete = done != null && done < total
      const message = incomplete
        ? `Only ${done}/${total} checklist items are complete. Mark ${setup.clinicDisplayName} as LIVE anyway?`
        : `Confirm ${setup.clinicDisplayName} as LIVE?`
      if (!window.confirm(message)) return
      handleFlagsUpdate({ isLive: true })
    } else {
      if (!window.confirm(`Mark ${setup.clinicDisplayName} as NOT live?`)) return
      handleFlagsUpdate({ isLive: false })
    }
  }

  // Role gate — tv_display has its own dedicated screen; everyone else
  // can at least view (backend enforces per-clinic access). Edit controls
  // are gated separately via canEdit.
  if (user?.role === 'tv_display') {
    return (
      <div className="container mx-auto px-4 py-12 text-center text-muted-foreground">
        <AlertCircle className="h-10 w-10 mx-auto mb-3 opacity-50" />
        <p>You don't have access to this page.</p>
      </div>
    )
  }

  // Super admin in "All Clinics" mode needs to pick a clinic
  if (!currentClinicId) {
    return (
      <div className="container mx-auto px-4 py-12 text-center text-muted-foreground">
        <Zap className="h-10 w-10 mx-auto mb-3 opacity-50" />
        <p>Select a clinic from the switcher above to view its express setup.</p>
      </div>
    )
  }

  const progress = setup?.progress || { done: 0, total: 0 }
  const progressPct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0

  // Open (not done) items by waiting party
  const openTasks = (setup?.tasks || []).filter(t => !['completed', 'na'].includes(t.status))
  const waitingCounts = {
    clinic: openTasks.filter(t => (t.waitingOn || 'clinic') === 'clinic').length,
    express: openTasks.filter(t => t.waitingOn === 'express').length,
  }
  const visibleTasks = (setup?.tasks || [])
    .filter(t =>
      waitingFilter === 'all' ||
      (!['completed', 'na'].includes(t.status) && (t.waitingOn || 'clinic') === waitingFilter)
    )
    // Open tasks first (template order), completed/na sink to the bottom
    .sort((a, b) => {
      const aDone = ['completed', 'na'].includes(a.status) ? 1 : 0
      const bDone = ['completed', 'na'].includes(b.status) ? 1 : 0
      if (aDone !== bDone) return aDone - bDone
      return a.order - b.order
    })

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
            <Zap className="h-7 w-7" />
            Express Setup
          </h1>
          <p className="text-muted-foreground mt-1">
            {setup?.clinicDisplayName || currentClinicId} — go-live checklist
          </p>
        </div>
        {setup && (
          <div className="flex items-center gap-3 flex-wrap">
            {setup.isLive ? (
              <Badge className="bg-green-600 hover:bg-green-600 text-white gap-1">
                <Rocket className="h-3 w-3" /> Live
                {setup.liveConfirmedAt && ` since ${formatDate(setup.liveConfirmedAt)}`}
              </Badge>
            ) : (
              <Badge variant="secondary">Not Live</Badge>
            )}
            {isSuperAdmin && (
              <>
                <Button variant="outline" size="sm" onClick={() => handleFlagsUpdate({ isExpress: !setup.isExpress })}>
                  {setup.isExpress ? 'Disable Express' : 'Enable Express'}
                </Button>
                <Button
                  size="sm"
                  variant={setup.isLive ? 'outline' : 'default'}
                  onClick={handleToggleLive}
                >
                  {setup.isLive ? 'Mark Not Live' : 'Mark Live'}
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg border border-destructive/50 bg-destructive/10 text-destructive text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Loading setup checklist...
        </div>
      ) : !setup ? null : !setup.isExpress ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <p>{setup.clinicDisplayName} is not flagged as an express clinic.</p>
            {isSuperAdmin && (
              <p className="text-sm mt-2">Use the "Enable Express" button above to turn it back on.</p>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Progress */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  Setup Checklist
                </CardTitle>
                <span className="text-sm text-muted-foreground">
                  {progress.done} of {progress.total} complete
                </span>
              </div>
              <CardDescription>
                Check off items as they're completed and use the notes field to provide the requested information.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              {/* Who is waiting on what */}
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                {[
                  { value: 'all', label: `All (${setup.tasks.length})` },
                  { value: 'clinic', label: `Waiting on Clinic (${waitingCounts.clinic})` },
                  { value: 'express', label: `Waiting on 5axis (${waitingCounts.express})` },
                ].map((chip) => (
                  <button
                    key={chip.value}
                    type="button"
                    onClick={() => setWaitingFilter(chip.value)}
                    className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                      waitingFilter === chip.value
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Tasks */}
          <div className="space-y-3">
            {visibleTasks.map((task) => (
              <TaskRow
                key={task.key}
                task={task}
                onUpdate={handleTaskUpdate}
                onUpload={handleUpload}
                onDownload={handleDownload}
                onDeleteAttachment={handleDeleteAttachment}
                onDeleteTask={handleDeleteTask}
                onVerifySite={handleVerifySite}
                saving={savingKey === task.key}
                isSuperAdmin={isSuperAdmin}
                canEdit={canEdit}
              />
            ))}
            {visibleTasks.length === 0 && (
              <p className="text-sm text-muted-foreground py-8 text-center">
                No open items waiting on {waitingFilter === 'clinic' ? 'the clinic' : '5axis'} 🎉
              </p>
            )}

            {/* Custom task creation (super_admin) */}
            {isSuperAdmin && (
              showAddTask ? (
                <AddTaskForm onAdd={handleAddTask} onCancel={() => setShowAddTask(false)} />
              ) : (
                <Button type="button" variant="outline" size="sm" onClick={() => setShowAddTask(true)}>
                  + Add Custom Task
                </Button>
              )
            )}
          </div>

          {/* Activity log */}
          <Card>
            <CardHeader className="pb-3 cursor-pointer select-none" onClick={handleToggleActivity}>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Activity</CardTitle>
                <span className="text-sm text-muted-foreground">
                  {(setup.activity || []).length} changes {showActivity ? '▾' : '▸'}
                </span>
              </div>
            </CardHeader>
            {showActivity && (
              <CardContent>
                {(setup.activity || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2">No changes recorded yet</p>
                ) : (
                  <>
                    <ul className="space-y-2">
                      {[...setup.activity].reverse().slice(0, activityLimit).map((entry, i) => (
                        <li key={i} className="text-sm flex items-baseline gap-2 flex-wrap">
                          <span className="text-xs text-muted-foreground whitespace-nowrap w-32 shrink-0">
                            {formatDateTime(entry.at)}
                          </span>
                          <span className="font-medium text-foreground">{entry.by}</span>
                          <span className="text-muted-foreground">{describeActivity(entry)}</span>
                        </li>
                      ))}
                    </ul>
                    {(setup.activity || []).length > activityLimit && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="mt-3"
                        onClick={() => setActivityLimit(l => l + 50)}
                      >
                        Show more ({setup.activity.length - activityLimit} older)
                      </Button>
                    )}
                  </>
                )}
              </CardContent>
            )}
          </Card>
        </>
      )}
    </div>
  )
}
